// src/features/system/orchestrator/components/ScopeKpiPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Scope KPI Analytics — namespace-driven KPI dashboard.
//
// Flow: select namespace → see aggregate KPIs for all keys → optionally
// drill into a specific key for focused metrics.
//
// Data sources:
//   • /bff/orchestrator/scopes/namespaces  → namespace list + counts
//   • /bff/orchestrator/scopes             → scope rows for selected ns
//   • /bff/orchestrator/hot-stream/history → event time-series for KPIs
// ────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { bff } from '@/lib/http/bff';
import {
  fetchScopeNamespacesApi,
  fetchWorkflowScopesApi,
  type ScopeNamespaceCount,
  type WorkflowScope,
} from '../api/scopesApi';

// ── Types ─────────────────────────────────────────────────────────────────

interface HotStreamEvent {
  scope_ns: string;
  scope_key: string;
  id_category: number;
  id_type: number;
  v_integer?: number;
  v_floatpoint?: number;
  v_string?: string;
  v_jsonb?: any;
  ts?: string;
  created_at?: string;
}

interface ScopeKPIs {
  totalEvents: number;
  uniqueKeys: number;
  uniqueCategories: number;
  uniqueTypes: number;
  avgEventsPerKey: number;
  latestEvent: string | null;
  oldestEvent: string | null;
  categoryCounts: Array<{ category: number; count: number }>;
  topKeys: Array<{ key: string; count: number }>;
}

// ── Component ─────────────────────────────────────────────────────────────

export default function ScopeKpiPanel() {
  const [selectedNs, setSelectedNs]   = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // 1. Fetch namespaces
  const { data: nsData, isLoading: nsLoading } = useQuery({
    queryKey: ['scope-kpi', 'namespaces'],
    queryFn: () => fetchScopeNamespacesApi(),
  });

  const namespaces: ScopeNamespaceCount[] = nsData?.items ?? [];

  // 2. Fetch scopes for selected namespace
  const { data: scopesData, isLoading: scopesLoading } = useQuery({
    queryKey: ['scope-kpi', 'scopes', selectedNs],
    queryFn: () => fetchWorkflowScopesApi({ scope_namespace: selectedNs!, limit: 500 }),
    enabled: !!selectedNs,
  });

  const scopeRows: WorkflowScope[] = useMemo(() => {
    const raw = scopesData?.items ?? scopesData?.rows ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [scopesData]);

  // 3. Fetch hot-stream events for namespace (or specific key)
  const hsQueryKey = selectedKey
    ? ['scope-kpi', 'hot-stream', selectedNs, selectedKey]
    : ['scope-kpi', 'hot-stream', selectedNs];

  const { data: hsData, isLoading: hsLoading } = useQuery({
    queryKey: hsQueryKey,
    queryFn: () => {
      const params: Record<string, string> = {
        scope_ns: selectedNs!,
        limit: '1000',
      };
      if (selectedKey) params.scope_key = selectedKey;
      const qs = new URLSearchParams(params).toString();
      return bff.get<{ items: HotStreamEvent[] }>(`/orchestrator/hot-stream/history?${qs}`);
    },
    enabled: !!selectedNs,
  });

  const events: HotStreamEvent[] = useMemo(() => {
    const raw = hsData?.items ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [hsData]);

  // 4. Compute KPIs
  const kpis = useMemo<ScopeKPIs>(() => {
    if (!events.length) {
      return {
        totalEvents: 0, uniqueKeys: 0, uniqueCategories: 0, uniqueTypes: 0,
        avgEventsPerKey: 0, latestEvent: null, oldestEvent: null,
        categoryCounts: [], topKeys: [],
      };
    }

    const keySet  = new Set<string>();
    const catSet  = new Set<number>();
    const typeSet = new Set<number>();
    const keyCounts = new Map<string, number>();
    const catCounts = new Map<number, number>();
    let latest: string | null = null;
    let oldest: string | null = null;

    for (const e of events) {
      const k = e.scope_key;
      keySet.add(k);
      catSet.add(e.id_category);
      typeSet.add(e.id_type);
      keyCounts.set(k, (keyCounts.get(k) ?? 0) + 1);
      catCounts.set(e.id_category, (catCounts.get(e.id_category) ?? 0) + 1);

      const ts = e.ts ?? e.created_at ?? null;
      if (ts) {
        if (!latest || ts > latest) latest = ts;
        if (!oldest || ts < oldest) oldest = ts;
      }
    }

    const totalEvents = events.length;
    const uniqueKeys  = keySet.size;

    const categoryCounts = Array.from(catCounts.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);

    const topKeys = Array.from(keyCounts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);

    return {
      totalEvents,
      uniqueKeys,
      uniqueCategories: catSet.size,
      uniqueTypes: typeSet.size,
      avgEventsPerKey: uniqueKeys > 0 ? Math.round(totalEvents / uniqueKeys) : 0,
      latestEvent: latest,
      oldestEvent: oldest,
      categoryCounts,
      topKeys,
    };
  }, [events]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleNsSelect = useCallback((ns: string) => {
    setSelectedNs(ns);
    setSelectedKey(null);
  }, []);

  const handleKeySelect = useCallback((key: string | null) => {
    setSelectedKey(key);
  }, []);

  const isLoading = nsLoading || scopesLoading || hsLoading;

  return (
    <div className="h-full flex gap-4 min-h-0 overflow-hidden">
      {/* ── Left: Namespace list ────────────────────────────────── */}
      <div className="w-56 shrink-0 flex flex-col min-h-0 rounded-lg border border-neutral-700/40 pac-glass">
        <div className="px-3 py-2 border-b border-neutral-700/30">
          <p className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Namespaces</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {nsLoading ? (
            <p className="text-xs text-neutral-600 px-3 py-4">Loading…</p>
          ) : namespaces.length === 0 ? (
            <p className="text-xs text-neutral-600 px-3 py-4">No namespaces found</p>
          ) : (
            namespaces.map(ns => (
              <button
                key={ns.scope_namespace}
                onClick={() => handleNsSelect(ns.scope_namespace)}
                className={`w-full text-left px-3 py-2 text-xs transition-colors border-b border-neutral-800/30 ${
                  selectedNs === ns.scope_namespace
                    ? 'bg-neutral-700/40 text-neutral-100'
                    : 'text-neutral-400 hover:bg-neutral-800/30 hover:text-neutral-200'
                }`}
              >
                <span className="font-mono block truncate">{ns.scope_namespace}</span>
                <span className="text-[10px] text-neutral-600">{ns.count} scope{ns.count !== 1 ? 's' : ''}</span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Center: KPI Cards + Key list ───────────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
        {!selectedNs ? (
          <div className="flex items-center justify-center h-full text-neutral-600 text-sm">
            Select a namespace to view KPIs
          </div>
        ) : (
          <>
            {/* Focused key banner */}
            {selectedKey && (
              <div className="flex items-center gap-2 rounded-lg border border-blue-800/30 bg-blue-950/20 px-3 py-2">
                <span className="text-[10px] text-blue-400 uppercase tracking-wider">Focused Key</span>
                <code className="text-xs text-blue-300 font-mono">{selectedKey}</code>
                <button
                  onClick={() => handleKeySelect(null)}
                  className="ml-auto text-[10px] text-neutral-500 hover:text-neutral-300"
                >
                  Show All
                </button>
              </div>
            )}

            {/* KPI grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              <KpiCard label="Total Events"   value={kpis.totalEvents.toLocaleString()}   accent="text-emerald-400" />
              <KpiCard label="Unique Keys"     value={String(kpis.uniqueKeys)}             accent="text-blue-400" />
              <KpiCard label="Categories"      value={String(kpis.uniqueCategories)}       accent="text-amber-400" />
              <KpiCard label="Event Types"     value={String(kpis.uniqueTypes)}            accent="text-purple-400" />
              <KpiCard label="Avg Events/Key"  value={String(kpis.avgEventsPerKey)}        accent="text-cyan-400" />
              <KpiCard
                label="Latest Event"
                value={kpis.latestEvent ? fmtRelative(kpis.latestEvent) : '–'}
                accent="text-neutral-300"
              />
              <KpiCard
                label="Oldest Event"
                value={kpis.oldestEvent ? fmtRelative(kpis.oldestEvent) : '–'}
                accent="text-neutral-300"
              />
              <KpiCard
                label="Time Span"
                value={kpis.latestEvent && kpis.oldestEvent ? fmtDuration(kpis.oldestEvent, kpis.latestEvent) : '–'}
                accent="text-zinc-300"
              />
            </div>

            {/* Category breakdown */}
            {kpis.categoryCounts.length > 0 && (
              <div className="rounded-lg border border-neutral-700/30 pac-glass-subtle px-4 py-3">
                <p className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider mb-2">
                  Events by Category
                </p>
                <div className="space-y-1">
                  {kpis.categoryCounts.map(c => {
                    const pct = kpis.totalEvents > 0 ? (c.count / kpis.totalEvents) * 100 : 0;
                    return (
                      <div key={c.category} className="flex items-center gap-2">
                        <span className="text-[10px] text-neutral-400 font-mono w-12 shrink-0">
                          cat {c.category}
                        </span>
                        <div className="flex-1 h-2 rounded-full bg-neutral-800 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-neutral-500"
                            style={{ width: `${Math.max(pct, 1)}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-neutral-500 font-mono w-16 text-right">
                          {c.count} ({pct.toFixed(0)}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top keys ranking → clickable to focus */}
            {kpis.topKeys.length > 0 && !selectedKey && (
              <div className="rounded-lg border border-neutral-700/30 pac-glass-subtle px-4 py-3">
                <p className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider mb-2">
                  Top Keys (click to focus)
                </p>
                <div className="max-h-60 overflow-y-auto space-y-0.5">
                  {kpis.topKeys.map((tk, i) => (
                    <button
                      key={tk.key}
                      onClick={() => handleKeySelect(tk.key)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-neutral-700/20 transition-colors text-left"
                    >
                      <span className="text-[10px] text-neutral-600 w-5 shrink-0 text-right">
                        {i + 1}.
                      </span>
                      <span className="text-xs text-neutral-300 font-mono flex-1 truncate">
                        {tk.key}
                      </span>
                      <span className="text-[10px] text-neutral-500 font-mono">
                        {tk.count} events
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Scope list */}
            {scopeRows.length > 0 && !selectedKey && (
              <div className="rounded-lg border border-neutral-700/30 pac-glass-subtle px-4 py-3">
                <p className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider mb-2">
                  Scopes ({scopeRows.length})
                </p>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-neutral-500 uppercase tracking-wider border-b border-neutral-700/30">
                        <th className="text-left px-2 py-1 font-medium">Key</th>
                        <th className="text-right px-2 py-1 font-medium">ID</th>
                        <th className="text-right px-2 py-1 font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scopeRows.slice(0, 50).map((s, i) => {
                        const key = s.scope_key ?? s.scopeKey ?? '';
                        return (
                          <tr
                            key={i}
                            onClick={() => handleKeySelect(key)}
                            className="border-b border-neutral-800/30 hover:bg-neutral-700/10 cursor-pointer"
                          >
                            <td className="px-2 py-1 text-neutral-300 font-mono truncate max-w-[200px]">
                              {key}
                            </td>
                            <td className="px-2 py-1 text-right text-neutral-500 font-mono">
                              {s.id_scope ?? '–'}
                            </td>
                            <td className="px-2 py-1 text-right text-neutral-500">
                              {s.created_at ? new Date(s.created_at).toLocaleDateString() : '–'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isLoading && (
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <span className="inline-block w-3 h-3 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
                Loading data…
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function KpiCard({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="rounded-lg border border-neutral-700/30 pac-glass-subtle px-3 py-2.5">
      <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-lg font-semibold font-mono ${accent}`}>{value}</p>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins   = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function fmtDuration(from: string, to: string): string {
  const ms   = new Date(to).getTime() - new Date(from).getTime();
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}
